/* eslint-disable react/jsx-indent */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useState, useEffect } from 'react';
import { shape, arrayOf, func, bool } from 'prop-types';
import colors from '@tcp/core/styles/themes/TCP/colors';
import { processPath } from '@tcp/core/src/utils';
import { SHOP_THIS_STORE_URL_QUERY_KEY } from '@tcp/core/src/constants/shopThisStore.constant';
import { getStoreFilterData } from '@tcp/core/src/components/features/browse/ShopThisStore/container/ShopThisStore.utils';
import Typography from '../../../../../../common/atoms/Typography';
import Anchor from '../../../../../../common/atoms/Anchor';
import RoleButton from '../../../../../../common/atoms/RoleButton';
import { SideNavContainer, ListBox, ArrowSpan } from '../styles/GlobalNavigationPanel.style';
import { callNavigateAPIForExternalLinks } from '../../../../../../../utils/utils';

const getCategoryData = (categories, categoryCap, enableCategoryCap, isOutfitPLPEnabled) => {
  return categories
    .filter((item) => {
      if (item?.outfitsPlp === 'TRUE') {
        return item.displayToCustomer && isOutfitPLPEnabled;
      }
      return item.displayToCustomer;
    })
    .map((category, index) => {
      return {
        ...category,
        hidden: enableCategoryCap && categoryCap && categoryCap > 0 ? index >= categoryCap : false,
      };
    });
};

const getGroupData = (groups, categoryCap) => {
  return groups.map((group, index) => {
    const isActive = group.subCategories?.some((item) => item.active === true) || false;
    return {
      ...group,
      active: isActive,
      shouldHighLight: isActive,
      hidden: categoryCap && categoryCap > 0 ? index >= categoryCap : false,
    };
  });
};

const shouldToggle = (item, index, categoryCap) => {
  return index >= categoryCap && item.active;
};

const checkExpandCollapse = (isShowMoreExpanded) => {
  if (!isShowMoreExpanded) {
    return 'show more';
  }
  return 'show less';
};

/**
 * component responsible for rendering navigation items under a group
 * has an in built behaviour of show more/less based on category cap value received from config props
 * @param {*} param0
 * @returns
 */

const GroupItems = ({
  categories = [],
  config: { categoryCap = undefined, labels: { showMore, showLess } } = {},
  hasSubCategory = false,
  enableCategoryCap = false,
  newFilterTrackAnalytics,
  isOutfitPLPEnabled,
  isShopThisStoreModeOn = false,
  filterList = [],
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const [isShowMoreExpanded, toggleShowMore] = useState(false);
  const [categoryData, updateActiveCategoryInData] = useState(() =>
    getCategoryData(categories, categoryCap, enableCategoryCap, isOutfitPLPEnabled)
  );
  useEffect(() => {
    updateActiveCategoryInData(
      getCategoryData(categories, categoryCap, enableCategoryCap, isOutfitPLPEnabled)
    );
  }, [categories]);
  const toggleShowMoreHandler = () => {
    const updatedCategoryData = categoryData.map((category, index) => {
      return {
        ...category,
        hidden: isShowMoreExpanded && index >= categoryCap,
      };
    });
    toggleShowMore((prevState) => {
      return !prevState;
    });
    updateActiveCategoryInData(updatedCategoryData);
  };

  useEffect(() => {
    categoryData.some((item, index) => {
      if (shouldToggle(item, index, categoryCap)) {
        toggleShowMoreHandler();
        return true;
      }
      return false;
    });
  }, []);

  const checkInList = (cat) => {
    if (isShopThisStoreModeOn) {
      return filterList.indexOf(cat) > -1;
    }
    return true;
  };

  const getSTSPath = (path, type) => {
    if (isShopThisStoreModeOn) {
      const stsUrl = `mode=${SHOP_THIS_STORE_URL_QUERY_KEY}&bopisStoreId=${getStoreFilterData()}`;
      if (type === 'to') {
        return `${path}&${stsUrl}`;
      }
      return `${path}?${stsUrl}`;
    }
    return path;
  };

  return (
    <>
      <ul className={hasSubCategory ? 'category-item--subcatgroup' : ''}>
        {categoryData.map((item) => {
          return item.hidden
            ? null
            : (item.displayToCustomer && checkInList(item.name) && (
                <li key={item.id}>
                  <Anchor
                    useRouterNavigation
                    updateRoute
                    to={processPath(getSTSPath(item.toPath, 'to'), 'to')}
                    asPath={processPath(getSTSPath(item.asPath, 'path'), 'path')}
                    className={`category-item-li ${item.active ? 'category-item--active' : ''}`}
                    active={item.active}
                  >
                    {item.name}
                  </Anchor>

                  {item.active && item.subCategories?.length > 0 && (
                    <GroupItems
                      categories={item.subCategories}
                      config={{ categoryCap, labels: { showMore, showLess } }}
                      hasSubCategory={item.subCategories?.length > 0}
                      newFilterTrackAnalytics={newFilterTrackAnalytics}
                      isOutfitPLPEnabled={isOutfitPLPEnabled}
                      isShopThisStoreModeOn={isShopThisStoreModeOn}
                      filterList={filterList}
                    />
                  )}
                </li>
              )) ||
                null;
        })}
      </ul>

      {enableCategoryCap && categoryData?.length > categoryCap && (
        <RoleButton
          className="cat-group--moretoggle"
          onClick={() => {
            newFilterTrackAnalytics({
              nav_cd171: `filter-category-${checkExpandCollapse(isShowMoreExpanded)}`,
              customEvents: ['event82'],
            });
            toggleShowMoreHandler();
          }}
          underlineType="default"
        >
          {!isShowMoreExpanded ? (
            <ArrowSpan className="cat-showMore">
              {showMore}
              <svg
                className="show-more-less"
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 1.44153L6 6.24658L1 1.44153"
                  stroke={colors.BUTTON.BLUE.LEFT_FILTERS_BTN}
                />
              </svg>
            </ArrowSpan>
          ) : (
            <ArrowSpan className="cat-showLess">
              {showLess}
              <svg
                className="show-more-less"
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 6.24646L6 1.44141L1 6.24646"
                  stroke={colors.BUTTON.BLUE.LEFT_FILTERS_BTN}
                />
              </svg>
            </ArrowSpan>
          )}
        </RoleButton>
      )}
    </>
  );
};

const Group = ({
  group,
  onGroupToggleHandler = () => {},
  config = {},
  newFilterTrackAnalytics,
  isOutfitPLPEnabled,
  isShopThisStoreModeOn = false,
  filterList = [],
}) => {
  const isGroupExpanded = group.active;

  return (
    <ListBox role="listbox" aria-labelledby={group.groupId} className="sideNav-listBox">
      <Typography variant="h2">
        <RoleButton
          id={group.groupId}
          onClick={() => {
            onGroupToggleHandler(group.groupId);
          }}
          underlineType={isGroupExpanded ? 'padding' : 'none'}
          active={group.active}
          shouldHighLight={group.shouldHighLight}
          className="sideNav-subHeader"
        >
          {group.groupName}
        </RoleButton>
      </Typography>

      {isGroupExpanded && (
        <GroupItems
          categories={group.subCategories}
          config={config}
          hasSubCategory={group.subCategories?.length > 0}
          enableCategoryCap={true}
          newFilterTrackAnalytics={newFilterTrackAnalytics}
          isOutfitPLPEnabled={isOutfitPLPEnabled}
          isShopThisStoreModeOn={isShopThisStoreModeOn}
          filterList={filterList}
        />
      )}
    </ListBox>
  );
};

const openDefaultL1 = (groupsData, onGroupToggle) => {
  if (!groupsData?.length) {
    return;
  }
  let activeFlag = false;
  for (let i = 0; i < groupsData?.length; i += 1) {
    if (groupsData[i]?.active) {
      activeFlag = true;
      break;
    }
  }
  if (!activeFlag) {
    onGroupToggle(groupsData[0]?.groupId);
  }
};

const getUpdatedData = (prevGroupsData, groupId) => {
  if (prevGroupsData.find((group) => group?.active)) {
    return prevGroupsData;
  }
  return prevGroupsData?.map((groupItem) => {
    return {
      ...groupItem,
      active: groupItem.groupId === groupId ? !groupItem.active : false,
    };
  });
};

const GlobalNavigationPanel = ({
  groups = [],
  config: { categoryCap = undefined, labels: { showMore, showLess } } = {},
  newFilterTrackAnalytics,
  isOutfitPLPEnabled,
  isShopThisStoreModeOn = false,
  filterList = [],
  breadCrumbs = [],
  getL2AndL1Categories,
  defaultStore,
  router,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const [groupsData, updateActiveGroupInData] = useState(() => getGroupData(groups, categoryCap));
  const [isShowMoreExpanded, toggleShowMore] = useState(false);
  useEffect(() => {
    if (breadCrumbs && breadCrumbs.length > 1 && isShopThisStoreModeOn) {
      const storeId = router.query?.storeNameId?.split('-')[4] || defaultStore?.basicInfo?.id;
      getL2AndL1Categories(storeId, breadCrumbs);
    }
  }, [breadCrumbs]);

  useEffect(() => {
    updateActiveGroupInData(getGroupData(groups, categoryCap));
  }, [groups, categoryCap]);

  const onGroupToggle = (groupId) => {
    setTimeout(() => {
      const updatedGroupData = groupsData?.map((group) => {
        return {
          ...group,
          active: group.groupId === groupId ? !group.active : false,
        };
      });

      updateActiveGroupInData(updatedGroupData);
    }, 0);
  };

  const onGroupToggleInitial = (groupId) => {
    updateActiveGroupInData((prevGroupsData) => {
      return getUpdatedData(prevGroupsData, groupId);
    });
  };

  const toggleShowMoreHandler = () => {
    const updatedGroupData = groupsData.map((group, index) => {
      return {
        ...group,
        hidden: isShowMoreExpanded && index >= categoryCap,
      };
    });
    toggleShowMore((prevState) => {
      return !prevState;
    });
    updateActiveGroupInData(updatedGroupData);
  };

  useEffect(() => {
    openDefaultL1(groupsData, onGroupToggleInitial);
  }, [groupsData]);

  const GroupWiseCategoryNavigation = groupsData.map((group) => {
    return group.hidden ? null : (
      <>
        {group.groupName ? (
          <Group
            key={group.groupId}
            group={group}
            onGroupToggleHandler={onGroupToggle}
            config={{ categoryCap, labels: { showMore, showLess } }}
            newFilterTrackAnalytics={newFilterTrackAnalytics}
            isOutfitPLPEnabled={isOutfitPLPEnabled}
            isShopThisStoreModeOn={isShopThisStoreModeOn}
            filterList={filterList}
          />
        ) : (
          <GroupItems
            categories={group.subCategories}
            config={{ categoryCap, labels: { showMore, showLess } }}
            hasSubCategory={group.subCategories?.length > 0}
            enableCategoryCap={true}
            newFilterTrackAnalytics={newFilterTrackAnalytics}
            isOutfitPLPEnabled={isOutfitPLPEnabled}
            isShopThisStoreModeOn={isShopThisStoreModeOn}
            filterList={filterList}
          />
        )}
      </>
    );
  });

  return (
    <SideNavContainer className="sidenav" onClick={callNavigateAPIForExternalLinks}>
      <nav className="sidenav--list" aria-label="Secondary">
        {GroupWiseCategoryNavigation}
        {groupsData.length > categoryCap && (
          <RoleButton
            className=""
            onClick={() => {
              newFilterTrackAnalytics({
                nav_cd171: `filter-category- ${checkExpandCollapse(isShowMoreExpanded)}`,
                customEvents: ['event82'],
              });
              toggleShowMoreHandler();
            }}
            underlineType="default"
          >
            {!isShowMoreExpanded ? showMore : showLess}
          </RoleButton>
        )}
      </nav>
    </SideNavContainer>
  );
};

GlobalNavigationPanel.propTypes = {
  config: shape({}).isRequired,
  groups: arrayOf(shape({})).isRequired,
};

Group.propTypes = {
  group: shape({}).isRequired,
  onGroupToggleHandler: func.isRequired,
  config: shape({}).isRequired,
};

GroupItems.propTypes = {
  categories: arrayOf(shape({})).isRequired,
  config: shape({}).isRequired,
  hasSubCategory: bool.isRequired,
  enableCategoryCap: bool,
};
GroupItems.defaultProps = {
  enableCategoryCap: false,
};

export default GlobalNavigationPanel;
